import { defineComponent, ref, onMounted, onUnmounted, watch } from 'vue';
import Sidebar from '@/components/Sidebar.vue';
import Header from '@/components/HeaderDashboard.vue';
import Swal from 'sweetalert2';
import { TrashIcon, PrinterIcon, PackageIcon, ShoppingCartIcon, RefreshCwIcon, CheckCircleIcon, HomeIcon, ClockIcon, UserIcon, CheckIcon, LockIcon, UndoIcon, ScanIcon } from 'lucide-vue-next';
import { OrdenVentaService } from '@/services/OrdenVentaService';
import { NotaCreditoService } from '@/services/NotaCreditoService';
export default defineComponent({
    name: 'VentasRetailComanda',
    components: {
        Sidebar, Header, TrashIcon, PrinterIcon, PackageIcon, ShoppingCartIcon, RefreshCwIcon,
        CheckCircleIcon, HomeIcon, ClockIcon, UserIcon, CheckIcon, LockIcon, UndoIcon, ScanIcon
    },
    setup() {
        const ordenes = ref([]);
        const reingresos = ref([]);
        const loading = ref(true);
        const view = ref('despachos');
        const itemsScanned = ref([]); // Formato: "ordenId-productoId"
        // Scanner Modal States
        const scanningOrder = ref(null);
        const barcodeManual = ref("");
        const scannerInput = ref(null);
        const openScanner = (orden) => {
            scanningOrder.value = orden;
            setTimeout(() => {
                if (scannerInput.value)
                    scannerInput.value.focus();
            }, 300);
        };
        const closeScanner = () => {
            scanningOrder.value = null;
            barcodeManual.value = "";
        };
        const handleScanInModal = () => {
            if (!scanningOrder.value || !barcodeManual.value)
                return;
            const code = barcodeManual.value.trim();
            const detalle = scanningOrder.value.detalles.find(d => d.codigoBarra === code);
            if (detalle) {
                const key = `${scanningOrder.value.ordenVentaId}-${detalle.productoId}`;
                if (!itemsScanned.value.includes(key)) {
                    itemsScanned.value.push(key);
                    // Sound
                    const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3");
                    audio.play().catch(() => { });
                }
            }
            else {
                // Alerta de no coincide
                Swal.fire({ toast: true, position: 'top-end', icon: 'error', title: 'Código no pertenece a esta orden', showConfirmButton: false, timer: 1500 });
            }
            barcodeManual.value = "";
            if (scannerInput.value)
                scannerInput.value.focus();
        };
        const itemConfirmado = (ordenId, productoId) => {
            return itemsScanned.value.includes(`${ordenId}-${productoId}`);
        };
        const ordenLista = (orden) => {
            return orden.detalles.every(d => itemConfirmado(orden.ordenVentaId, d.productoId));
        };
        const countsConfirmados = (orden) => {
            return orden.detalles.filter(d => itemConfirmado(orden.ordenVentaId, d.productoId)).length;
        };
        const confirmarItemManual = (ordenId, productoId) => {
            const key = `${ordenId}-${productoId}`;
            if (!itemsScanned.value.includes(key)) {
                itemsScanned.value.push(key);
            }
        };
        onMounted(async () => {
            cargarDatos();
        });
        onUnmounted(() => {
        });
        watch(view, () => { cargarDatos(); });
        const cargarDatos = async () => {
            loading.value = true;
            try {
                if (view.value === 'despachos') {
                    const res = await OrdenVentaService.getComandas();
                    if (res.exito)
                        ordenes.value = res.datos;
                }
                else {
                    const res = await NotaCreditoService.getAll();
                    if (res.exito)
                        reingresos.value = res.datos.filter(n => n.estado === 'PendienteAlmacen');
                }
            }
            catch (error) {
                console.error(error);
            }
            finally {
                loading.value = false;
            }
        };
        const despachar = async (orden) => {
            try {
                const confirm = await Swal.fire({
                    title: 'Confirmar retiro',
                    text: `¿Entregar al cliente la órden ${orden.codigoOrden}?`,
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonColor: '#10b981',
                    confirmButtonText: 'Sí, Entregar'
                });
                if (confirm.isConfirmed) {
                    Swal.showLoading();
                    const res = await OrdenVentaService.cambiarEstado(orden.ordenVentaId, 'Despachada');
                    if (res.exito) {
                        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Despachado', showConfirmButton: false, timer: 1500 });
                        cargarDatos();
                    }
                }
            }
            catch (error) {
                Swal.fire('Error', 'No se pudo despachar', 'error');
            }
        };
        const confirmarRecibo = async (nc) => {
            try {
                const confirm = await Swal.fire({
                    title: 'Confirmar Reingreso',
                    text: `¿Recibió físicamente los repuestos de la NC ${nc.codigoNota}? Esto desbloqueará la caja.`,
                    icon: 'info',
                    showCancelButton: true,
                    confirmButtonText: 'Sí, Recibido',
                    confirmButtonColor: '#f59e0b'
                });
                if (confirm.isConfirmed) {
                    Swal.showLoading();
                    const res = await NotaCreditoService.confirmarReingreso(nc.notaCreditoId);
                    if (res.exito) {
                        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Recibido y Desbloqueado', showConfirmButton: false, timer: 3000 });
                        cargarDatos();
                    }
                }
            }
            catch (error) {
                Swal.fire('Error', 'No se pudo confirmar', 'error');
            }
        };
        const anularOrden = async (orden) => {
            try {
                const confirm = await Swal.fire({
                    title: '¿Anular Orden?',
                    text: `La mercancía volverá al inventario disponible.`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#ef4444',
                    confirmButtonText: 'Sí, Anular'
                });
                if (confirm.isConfirmed) {
                    Swal.showLoading();
                    const res = await OrdenVentaService.anular(orden.ordenVentaId);
                    if (res.exito) {
                        Swal.fire({ toast: true, position: 'top-end', icon: 'error', title: 'Anulada', showConfirmButton: false, timer: 1500 });
                        cargarDatos();
                    }
                }
            }
            catch (error) {
                Swal.fire('Error', 'No se pudo anular', 'error');
            }
        };
        const imprimirTicket = (orden) => {
            const printWindow = window.open('', '_blank', 'width=400,height=600');
            if (!printWindow)
                return;
            const html = `
        <html>
          <head>
            <title>Comanda ${orden.codigoOrden}</title>
            <style>
              body { font-family: 'Courier New', Courier, monospace; width: 80mm; padding: 10px; margin: 0; }
              .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
              .item { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 14px; }
              .footer { border-top: 1px dashed #000; margin-top: 10px; padding-top: 10px; text-align: center; font-size: 12px; }
              h2, h4 { margin: 5px 0; }
            </style>
          </head>
          <body>
            <div class="header">
              <h2>SURTIMOTOS</h2>
              <h4>${orden.nombreSucursal || 'Almacén Principal'}</h4>
              <p>Comanda: <strong>${orden.codigoOrden}</strong></p>
              <p>Fecha: ${new Date(orden.fechaCreacion).toLocaleString()}</p>
            </div>
            <div>
              <p><strong>Cliente:</strong> ${orden.nombreCliente}</p>
              <div style="border-bottom: 1px solid #000; margin: 10px 0;"></div>
              ${orden.detalles.map(d => `
                <div class="item">
                  <span>${d.nombreProducto}</span>
                  <strong>x${d.cantidad}</strong>
                </div>
              `).join('')}
            </div>
            <div class="footer">
              <p>*** COPIA PARA ALMACÉN ***</p>
            </div>
          </body>
        </html>
      `;
            printWindow.document.write(html);
            printWindow.document.close();
            printWindow.print();
        };
        return {
            ordenes, reingresos, loading, view, despachar, anularOrden, imprimirTicket, confirmarRecibo,
            itemConfirmado, ordenLista, countsConfirmados, fetchDataSse: cargarDatos, confirmarItemManual,
            scanningOrder, barcodeManual, scannerInput, openScanner, closeScanner, handleScanInModal
        };
    }
});
